import { CameraMode, ClipTask } from '../defines';
import { DistanceAnnotation } from '../utils/PointoramaAnnotation';
import { PointoramaPlane } from '../utils/PointoramaPlane';
import { PointoramaSurface } from '../utils/PointoramaSurface';
import { Viewer } from './viewer';
import * as THREE from 'three';
import { getViewerEdlEnabledProperty, getSurfacePlaneRotationProperty, hasGlobalPropertyService } from '@pointorama/potree-shared';
import type { Subscription } from '@pointorama/potree-shared';

export class ViewerMode {
  viewer: Viewer;
  in2DMode: boolean;

  associatedPlane: PointoramaPlane | null = null;
  associatedLine: DistanceAnnotation | null = null;
  hasVirtualPlane = false;

  /** While in a surface's 2D view, keep the camera roll synced to the surface's
   *  rotation property (e.g. edited in SurfaceProperties) — mirrors SurfaceToolBase. */
  private _surfaceRotationSubscription?: Subscription;

  constructor(viewer: Viewer) {
    this.viewer = viewer;

    this.in2DMode = false;
  }

  get canFlip(): boolean {
    return (this.hasVirtualPlane && !!this.associatedLine) || !!this.associatedPlane;
  }

  enable2DMode(identifier: string) {
    this.hasVirtualPlane = false;
    this.associatedLine = null;
    this.associatedPlane = null;
    this._surfaceRotationSubscription?.unsubscribe();
    this._surfaceRotationSubscription = undefined;

    if (identifier === 'map') {
      this.enable2DModeFromMap();
      return;
    }

    if (identifier === 'side') {
      // Side view: same as map but does NOT restore saved yaw
      this.enable2DModeFromSide();
      return;
    }

    const plane = this.viewer.scene.planes.find((p) => p.identifier === identifier);

    if (plane) {
      this.enable2DModeFromPlane(plane);
      return;
    }

    // Check surfaces - they have a rotation property that should be used for roll
    const surface = this.viewer.scene.surfaces?.find((s: PointoramaSurface) => s.identifier === identifier);

    if (surface) {
      this.enable2DModeFromSurface(surface);
      return;
    }

    const line = this.viewer.scene.measurements.find((l) => l.identifier === identifier);

    if (line && line instanceof DistanceAnnotation) {
      this.enable2DModeFromLine(line);
      return;
    }

    console.warn('[ViewerMode] No plane, surface, or line found for identifier:', identifier);
  }

  private enable2DModeFromMap() {
    this.in2DMode = true;

    const normal = new THREE.Vector3(0, 0, 1);
    const position = new THREE.Vector3();
    const pc = this.viewer.scene.pointclouds.at(0);
    if (pc) {
      position.addVectors(pc.boundingBox.min, pc.boundingBox.max).divideScalar(2).applyMatrix4(pc.matrixWorld);
      position.z = 0;
    }
    this.associatedPlane = new PointoramaPlane({ position, normal, clipTask: ClipTask.NONE });
    this.hasVirtualPlane = true;

    this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
    // EDL is suppressed by ViewManager.applyViewContext after this method returns.

    // Set top-down view orientation, applying the effective heading in the
    // same tick. The old code wrote roll=0, awaited auto-detection, then
    // wrote the real roll — a visible zero-roll window whose outcome
    // depended on point-cloud streaming, and a clobber target for concurrent
    // camera writes. Detection now runs in the background
    // (ensureDetectedHeading) and applies itself only when still safe.
    // Yaw is pinned to 0: the top-down class expresses its heading on ROLL
    // alone (yaw/roll are gimbal-degenerate at pitch −90°).
    const view = this.viewer.scene.view;
    view.pitch = -Math.PI / 2;
    view.yaw = 0;
    const heading = this.viewer.cameraOrientationManager?.effectiveHeading('top_down_2d');
    view.roll = heading !== undefined ? heading : 0;
    this.viewer.cameraOrientationManager?.ensureDetectedHeading();
  }

  /**
   * Enable 2D mode for side/surface views
   * Similar to map view but does NOT restore saved yaw rotation.
   * This preserves the current camera orientation for tools that work on arbitrary surfaces.
   */
  private enable2DModeFromSide() {
    this.in2DMode = true;

    const normal = new THREE.Vector3(0, 0, 1);
    const position = new THREE.Vector3();
    const pc = this.viewer.scene.pointclouds.at(0);
    if (pc) {
      position.addVectors(pc.boundingBox.min, pc.boundingBox.max).divideScalar(2).applyMatrix4(pc.matrixWorld);
      position.z = 0;
    }
    this.associatedPlane = new PointoramaPlane({ position, normal, clipTask: ClipTask.NONE });
    this.hasVirtualPlane = true;

    this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
    // EDL is suppressed by ViewManager.applyViewContext after this method returns.

    // Set top-down view orientation but do NOT restore saved roll
    // This allows the current view orientation to be preserved
    const view = this.viewer.scene.view;
    view.pitch = -Math.PI / 2;
    view.roll = 0;
    // Yaw is intentionally not modified - keeps current orientation
  }

  private async enable2DModeFromPlane(plane: PointoramaPlane) {
    this.viewer.hideMap();

    this.in2DMode = true;
    this.associatedPlane = plane;

    this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
    // EDL is suppressed by ViewManager.applyViewContext after this method returns.
    const view = this.viewer.scene.view;

    view.pitch = plane.inclination - Math.PI / 2;
    view.roll = 0;

    // For plane-aligned views, always use the plane's bearing to determine yaw
    // (saved rotation should only apply to top-down map views)
    view.yaw = 2 * Math.PI - plane.bearing;

    view.position.x = plane.position.x;
    view.position.y = plane.position.y;
    view.position.z = plane.position.z;
  }

  /**
   * Enable 2D mode from a surface, using its rotation property for the camera roll
   */
  private enable2DModeFromSurface(surface: PointoramaSurface) {
    this.viewer.hideMap();

    this.in2DMode = true;

    // Create a virtual plane from the surface for clipping. Reuse the surface's
    // identifier so toggle/plane-switching comparisons in ViewManager.applyViewContext
    // (which compare associatedPlane.identifier against viewContext.planeId) match.
    const surfacePlane = surface.getPlane();
    this.associatedPlane = new PointoramaPlane({
      identifier: surface.identifier,
      position: surfacePlane.position.clone(),
      normal: surfacePlane.normal.clone(),
      clipTask: ClipTask.SHOW_INSIDE,
    });
    this.hasVirtualPlane = true;

    this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
    // EDL is suppressed by ViewManager.applyViewContext after this method returns.
    const view = this.viewer.scene.view;

    // Calculate inclination and bearing from the surface normal
    const normal = surfacePlane.normal;
    const zAxis = new THREE.Vector3(0, 0, 1);
    const yAxis = new THREE.Vector3(0, 1, 0);

    const inclination = normal.angleTo(zAxis);

    let bearing = 0;
    if (inclination !== 0) {
      const bearingVector = normal.clone().negate();
      bearingVector.z = 0;
      if (bearingVector.lengthSq() > 0.0001) {
        bearingVector.normalize();
        bearing = bearingVector.angleTo(yAxis);
        const crossZ = yAxis.clone().cross(bearingVector).z;
        if (crossZ < 0) {
          bearing = 2 * Math.PI - bearing;
        }
      }
    }

    view.pitch = inclination - Math.PI / 2;
    view.yaw = 2 * Math.PI - bearing;

    // Use the surface's rotation property for the camera roll
    view.roll = surface.normalRotation;

    // Keep the camera roll synced to later rotation changes (e.g. edited in the
    // surface's properties panel) while this 2D view is active — mirrors the
    // rotation subscription in SurfaceToolBase. Unsubscribed in disable2DMode and
    // on the next enable2DMode.
    this.subscribeToSurfaceRotation(surface.identifier);

    // Position camera along the surface normal at the current distance
    // This is crucial for vertical/inclined surfaces
    const currentRadius = view.radius || 5;
    view.position.copy(surfacePlane.position.clone().add(normal.clone().multiplyScalar(currentRadius)));
  }

  /**
   * Subscribe to a surface's rotation property so the camera roll tracks it while
   * the surface's 2D view is active. Only applies the roll in orthographic mode.
   */
  private subscribeToSurfaceRotation(surfaceIdentifier: string): void {
    this._surfaceRotationSubscription?.unsubscribe();
    if (!hasGlobalPropertyService()) return;
    try {
      const rotationProperty = getSurfacePlaneRotationProperty(surfaceIdentifier);
      this._surfaceRotationSubscription = rotationProperty.subscribeToChanges((rotation) => {
        if (typeof rotation !== 'number') return;
        if (this.in2DMode && this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
          this.viewer.scene.view.roll = rotation;
        }
      });
    } catch {
      // Global property service not ready — initial roll above still applied.
    }
  }

  private enable2DModeFromLine(line: DistanceAnnotation) {
    const points = line.points.map((p) => p.position);

    if (points.length > 2) return;

    const position = new THREE.Vector3().addVectors(points[0], points[1]).divideScalar(2.0);
    const v0 = new THREE.Vector3(0, 0, points[0].z);
    const v1 = points[1].clone().sub(points[0]);
    const normal = new THREE.Vector3().crossVectors(v0, v1).normalize();
    this.associatedPlane = new PointoramaPlane({ position, normal, clipTask: ClipTask.SHOW_INSIDE });

    this.enable2DModeFromPlane(this.associatedPlane);
    this.hasVirtualPlane = true;
    this.associatedLine = line;
  }

  disable2DMode() {
    this.hasVirtualPlane = false;
    this.in2DMode = false;
    this.associatedPlane = null;
    this.associatedLine = null;
    this._surfaceRotationSubscription?.unsubscribe();
    this._surfaceRotationSubscription = undefined;
    this.viewer.hideMap();
    this.viewer.setCameraMode(CameraMode.PERSPECTIVE);
    // EDL is restored by ViewManager.applyViewContext after this method returns.
    // Note: Removed fitToScreen() call to allow seamless view transitions.
    // The ViewManager now handles camera state conversion when switching views.
  }

  /**
   * Turn EDL off while in a 2D view. Does NOT touch the user's saved preference —
   * the reactive property remains the source of truth so the toggle state
   * is preserved across view transitions.
   *
   * Only ViewManager.applyViewContext should call this; individual view modes
   * and tools must not manage EDL themselves. Pair with restoreEDLFromPreference()
   * when returning to 3D.
   */
  suppressEDLForMode(): void {
    this.viewer.setEDLEnabled(false);
  }

  /**
   * Restore EDL to the user's saved preference after leaving a 2D view.
   * Only ViewManager.applyViewContext should call this. Falls back to true if
   * the property service isn't ready, matching the property's registered default.
   */
  restoreEDLFromPreference(): void {
    this.viewer.setEDLEnabled(this.getSavedShowEdgesPreference());
  }

  private getSavedShowEdgesPreference(): boolean {
    try {
      if (!hasGlobalPropertyService()) return true;
      const value = getViewerEdlEnabledProperty().value;
      return typeof value === 'boolean' ? value : true;
    } catch {
      return true;
    }
  }
}
